import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../context/LoginContext';
import { useSettings } from '../../context/SettingsContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useModal } from '../../context/ModalContext';
import { useI18n } from '../../context/I18nContext';
import { ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE_BYTES, MAX_AVATAR_SIZE_MB } from '../../constants';
import { useRecovery, useRecoveryActions } from '../../context/RecoveryContext/index';
import { markRecoveryCompleted, isRecoveryCompleted } from '../../context/RecoveryContext/storage';
import useProfile from '../../hooks/useProfile';
import { useUserAvatar } from '../../hooks/useUserAvatar';
import { useUserDisplayName } from '../../hooks/useUserDisplayName';
import logger from '../../utils/logger';
import { generateInitialsAvatar } from '../../utils/avatar';
import DevicesPage from './Devices/DevicesPage';
import AutoLogoutPage from './AutoLogoutPage';
import ChatExpiryReminderPage from './ChatExpiryReminderPage';
import DeveloperInfoPage from './DeveloperInfoPage';
import VersionInfoPage from './VersionInfoPage';
import FirstContactPermissionsPage from './FirstContactPermissionsPage';
import IgnoredUsersPage from './IgnoredUsersPage';
import ChatBackupPage from './ChatBackupPage';
import NotificationsSettingsPage from './NotificationsSettingsPage';
import FHIRAccountPage from './FHIRAccountPage';
import SettingsToggle from './SettingsToggle';
import RecoveryKeyModal from '../Recovery/RecoveryKeyModal';
import RecoveryKeyValidator from '../../services/RecoveryKeyValidator';
import './SettingsModal.css';

const SettingsModal = ({ isOpen, onClose, initialPage = 'settings' }) => {
  const { currentUser, logout } = useLogin();
  const { settings, updateSetting } = useSettings();
  const { uploadAvatar, isUploading } = useProfile();
  const client = useMatrixClient();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();
  const navigate = useNavigate();
  const { showError } = useModal();
  const { t } = useI18n();
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [currentPage, setCurrentPage] = useState('settings'); // Track current page
  const [showRecoveryKeyModal, setShowRecoveryKeyModal] = useState(false); // For users with SSSS who need key

  // ---------------------------------------------------------------------------
  // Profile avatar rendering (defensive against broken <img> sources)
  // ---------------------------------------------------------------------------
  const userId = useMemo(
    () => currentUser?.id || currentUser?.matrixUserId,
    [currentUser?.id, currentUser?.matrixUserId],
  );
  
  // Fetch actual display name from Matrix client
  const fallbackName = currentUser?.name || userId || 'Guest';
  const displayName = useUserDisplayName(userId, fallbackName);
  const { avatarUrl } = useUserAvatar(userId, displayName, 96);

  const fallbackAvatar = useMemo(() => {
    try {
      return generateInitialsAvatar(displayName, 96);
    } catch {
      return null;
    }
  }, [displayName]);

  const [profileAvatarSrc, setProfileAvatarSrc] = useState(
    fallbackAvatar || '/images/AppIcon60x60.png',
  );

  // Reset to a safe fallback when the user changes (prevents showing revoked blob URLs)
  useEffect(() => {
    setProfileAvatarSrc(fallbackAvatar || '/images/AppIcon60x60.png');
  }, [fallbackAvatar]);

  // Prefer the authenticated blob/data URL from AvatarContext when available
  useEffect(() => {
    if (avatarUrl) {
      setProfileAvatarSrc(avatarUrl);
    }
  }, [avatarUrl]);

  const handleLogout = async () => {
    try {
      await logout();
      onClose(); // Close the modal after successful logout
      // Explicitly navigate to login page after logout
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed:', error);
      // Still close the modal and navigate to login even if logout fails
      onClose();
      navigate('/login');
    }
  };

  const saveScrollPosition = () => {
    if (scrollableContentRef.current) {
      scrollPositionRef.current = scrollableContentRef.current.scrollTop;
    }
  };

  const goToDevicesPage = () => {
    saveScrollPosition();
    setCurrentPage('devices');
  };
  const goToAutoLogoutPage = () => {
    saveScrollPosition();
    setCurrentPage('auto-logout');
  };
  const goToChatExpiryReminderPage = () => {
    saveScrollPosition();
    setCurrentPage('chat-expiry-reminder');
  };
  const goToVersionInfoPage = () => {
    saveScrollPosition();
    setCurrentPage('version-info');
  };
  const goToDeveloperInfoPage = () => {
    saveScrollPosition();
    setCurrentPage('developer-info');
  };
  const goToFirstContactPermissionsPage = () => {
    saveScrollPosition();
    setCurrentPage('first-contact-permissions');
  };
  const goToIgnoredUsersPage = () => {
    saveScrollPosition();
    setCurrentPage('ignored-users');
  };
  const goToNotificationsPage = () => {
    saveScrollPosition();
    setCurrentPage('notifications');
  };
  const goToFHIRAccountPage = () => {
    saveScrollPosition();
    setCurrentPage('fhir-account');
  };

  // Chat Backup click handler - show overlay modal for users with SSSS, else navigate to ChatBackupPage
  const handleChatBackupClick = useCallback(() => {
    // Check if recovery has been completed for this device (localStorage flag)
    // This takes precedence over recoveryState.needsRecovery which may be stale
    const deviceId = client?.getDeviceId();
    const hasCompleted = deviceId && isRecoveryCompleted(deviceId);

    // If user has SSSS/backup and needs to enter recovery key (and hasn't completed), show overlay modal
    const needsKey = !hasCompleted &&
      recoveryState?.needsRecovery &&
      (recoveryState?.hasSSSS || recoveryState?.hasBackup);

    if (needsKey) {
      logger.debug('[SettingsModal] Showing RecoveryKeyModal overlay - needsRecovery=true, hasCompleted=false');
      setShowRecoveryKeyModal(true);
    } else {
      // User either has no SSSS (needs setup) or already completed recovery
      logger.debug('[SettingsModal] Navigating to ChatBackupPage', { hasCompleted, needsRecovery: recoveryState?.needsRecovery });
      saveScrollPosition();
      setCurrentPage('chat-backup');
    }
  }, [client, recoveryState?.needsRecovery, recoveryState?.hasSSSS, recoveryState?.hasBackup]);

  // Recovery key modal handlers
  const handleRecoverySuccess = useCallback(async (result) => {
    logger.info('[SettingsModal] Recovery success', result);

    // Handle device verification completion
    if (result?.verified) {
      logger.info('[SettingsModal] Device verification succeeded - marking recovery complete');
      if (recoveryActions?.complete) {
        recoveryActions.complete();
      }
      const deviceId = client?.getDeviceId();
      if (deviceId) {
        markRecoveryCompleted(deviceId);
      }
    } else {
      // Handle recovery key entry (traditional flow)
      if (recoveryActions?.restore) {
        try {
          await recoveryActions.restore();
        } catch (error) {
          logger.error('[SettingsModal] Failed to restore after validation:', error);
        }
      }
    }

    // Close recovery modal
    setShowRecoveryKeyModal(false);
  }, [recoveryActions, client]);

  const handleRecoverySkip = useCallback(() => {
    logger.info('[SettingsModal] User skipped recovery');
    setShowRecoveryKeyModal(false);
    if (recoveryActions?.skip) {
      recoveryActions.skip();
    }
  }, [recoveryActions]);

  const handleRecoveryReset = useCallback(async () => {
    logger.info('[SettingsModal] User requested backup reset from RecoveryKeyModal');
    setShowRecoveryKeyModal(false);
    // Two-phase reset: Don't call recoveryActions.reset() here - that would prematurely
    // mark needsRecovery=false. ChatBackupPage will handle the actual reset in Phase 2
    // (when user copies/exports the new key to commit).
    // Set flag for ChatBackupPage to force key generation
    sessionStorage.setItem('forceKeyGeneration', 'true');
    // Navigate to Chat Backup page to show key generation flow (Phase 1)
    setCurrentPage('chat-backup');
  }, []);

  // Create validator for recovery key
  const recoveryValidator = {
    onSubmit: async (recoveryKey) => {
      try {
        const result = await RecoveryKeyValidator.validateAndSubmit(
          recoveryKey,
          client,
          recoveryActions?.dispatch,
        );
        return result;
      } catch (error) {
        logger.error('[SettingsModal] Recovery validation error:', error);
        return {
          success: false,
          error: error.code || 'UNKNOWN',
        };
      }
    },
  };

  const goBackToSettings = () => setCurrentPage('settings');

  // Set page when modal opens - use initialPage prop or default to 'settings'
  // CRITICAL: This fixes state persistence bug where modal could open on wrong page
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(initialPage);
      scrollPositionRef.current = 0; // Reset scroll position too
    }
  }, [isOpen, initialPage]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Focus the modal
      if (modalRef.current) {
        modalRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Effect to restore scroll position when returning to the main settings page
  useLayoutEffect(() => {
    if (currentPage === 'settings' && scrollableContentRef.current) {
      // Use useLayoutEffect to set scroll position before the browser paints
      scrollableContentRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [currentPage]);

  // Handle backdrop click
  const handleOverlayClick = (event) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleAvatarClick = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size BEFORE attempting upload
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showError(
        t('AvatarValidation.fileTooLargeTitle'),
        t('AvatarValidation.fileTooLarge', { maxSize: MAX_AVATAR_SIZE_MB, fileSize: fileSizeMB }),
      );
      event.target.value = '';
      return;
    }

    // Validate file type - only allow specific image types for security
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showError(
        t('AvatarValidation.invalidFileTypeTitle'),
        t('AvatarValidation.mustBeImage'),
      );
      event.target.value = '';
      return;
    }

    try {
      await uploadAvatar(file);
      logger.info('Avatar uploaded successfully');
      // Note: The avatar will update automatically via Matrix sync events
      // which will trigger a re-render through the MatrixContext
    } catch (error) {
      logger.error('Failed to upload avatar:', error);
      showError(
        t('AvatarValidation.uploadFailedTitle'),
        error.message || t('AvatarValidation.uploadFailed'),
      );
    }

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <>
    <div
      className="settings-modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/gif,image/webp"
      />
      <div
        className="settings-modal"
        ref={modalRef}
        role="dialog"
        aria-labelledby="settings-modal-title"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="settings-content-wrapper">
          {/* Left Profile Section */}
          <div className="settings-profile-section">
            <div
              className="profile-image-container"
              style={{ cursor: 'pointer', position: 'relative' }}
              title={t('SettingsModal.clickToChangeAvatar', {}, 'Click to change avatar')}
            >
              <img
                src={profileAvatarSrc}
                alt={t('SettingsModal.profileAlt', {}, 'Profile')}
                className="profile-image"
                onClick={handleAvatarClick}
                onError={() => setProfileAvatarSrc(fallbackAvatar || '/images/AppIcon60x60.png')}
                style={{ opacity: isUploading ? 0.5 : 1 }}
              />
              <img
                src="/images/ChangeProfileImageBadge.png"
                alt={t('SettingsModal.changeAvatar', {}, 'Change Avatar')}
                className="change-avatar-badge"
                onClick={handleAvatarClick}
                style={{ opacity: isUploading ? 0.5 : 1 }}
              />
              {isUploading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)',
                }}>
                  {t('SettingsModal.uploading', {}, 'Uploading...')}
                </div>
              )}
            </div>
            <div className="profile-name">{displayName}</div>

            <div className="profile-form">
              <div className="form-group">
                <label className="form-label">{t('SettingsModal.displayNameLabel', {}, 'Display Name')}</label>
                <input
                  type="text"
                  className="form-input"
                  readOnly
                  value={displayName}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('SettingsModal.userIdLabel', {}, 'User ID')}</label>
                <input
                  type="email"
                  className="form-input"
                  readOnly
                  defaultValue={currentUser?.username || currentUser?.matrixUserId || ''}
                />
              </div>
            </div>

            <div className="profile-logout">
              <button className="logout-button" onClick={handleLogout}>
                <ion-icon name="log-out-outline" />
                {t('SettingsModal.logout', {}, 'Logout')}
              </button>
            </div>
          </div>

          {/* Right Settings Panel */}
          <div className="settings-right-panel">
            <div className="settings-modal-header-centered">
              <h2 id="settings-modal-title">{t('SettingsModal.modalTitle', {}, 'Settings')}</h2>
              <button
                className="settings-modal-close"
                onClick={onClose}
                aria-label={t('SettingsModal.closeAriaLabel', {}, 'Close settings')}
              >
                ×
              </button>
            </div>
            <hr className="settings-header-divider" />

            <div
              className="settings-scrollable-content"
              ref={scrollableContentRef}
            >
              <div className={`settings-page ${currentPage === 'settings' ? 'active' : ''}`}>
                <div className="settings-sections">
                  <div className="settings-section">
                    <div className="settings-section-header">{t('SettingsModal.privacySection', {}, 'Privacy')}</div>
                    <div className="settings-section-content">
                      <SettingsToggle
                        icon="fa-eye"
                        label={t('SettingsModal.sendReadReceipts', {}, 'Send Read Receipts')}
                        checked={settings.sendPublicReadReceipts}
                        onChange={(value) => updateSetting('sendPublicReadReceipts', value)}
                      />
                      <SettingsToggle
                        icon="fa-keyboard"
                        label={t('SettingsModal.sendTypingIndicators', {}, 'Send Typing Indicators')}
                        checked={settings.sendTypingIndicators}
                        onChange={(value) => updateSetting('sendTypingIndicators', value)}
                      />
                      <SettingsToggle
                        icon="fa-user-secret"
                        label={t('SettingsModal.alwaysAppearOffline', {}, 'Always Appear Offline')}
                        checked={settings.alwaysAppearOffline}
                        onChange={(value) => updateSetting('alwaysAppearOffline', value)}
                      />
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">{t('VzdDirectory.pageTitle')}</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToFHIRAccountPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/healthcare-dir.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('VzdDirectory.pageTitle')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">{t('SettingsModal.notificationsSection', {}, 'Notifications')}</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToNotificationsPage}
                      >
                        <div className="settings-line-button-content">
                          <ion-icon name="notifications-outline" class="settings-line-icon-ionicon" />
                          <span>{t('SettingsModal.browserNotificationsButton', {}, 'Browser Notifications')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">{t('SettingsModal.securitySection', {}, 'Security')}</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToAutoLogoutPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/automatic-logout-timer-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.automaticLogoutTimer', {}, 'Automatic Logout Timer')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button"
                        onClick={goToChatExpiryReminderPage}
                      >
                        <div className="settings-line-button-content">
                          <ion-icon name="time-outline" class="settings-line-icon-ionicon" />
                          <span>{t('ChatExpiryReminderPage.title', {}, 'Chat Expiry Reminders')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button my-devices-button"
                        onClick={goToDevicesPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/my-devices-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.myDevices', {}, 'My Devices')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button"
                        onClick={handleChatBackupClick}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/chat-backup-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.chatBackupAndKeys', {}, 'Chat Backup and Keys')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button"
                        onClick={goToFirstContactPermissionsPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/first-contact-permissions-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.incomingInvites', {}, 'Incoming Invites')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button"
                        onClick={goToIgnoredUsersPage}
                      >
                        <div className="settings-line-button-content">
                          <ion-icon name="person-remove-outline" class="settings-line-icon-ionicon" />
                          <span>{t('SettingsModal.ignoredUsers', {}, 'Ignored Users')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>
                  <div className="settings-section">
                    <div className="settings-section-header">{t('SettingsModal.helpSection', {}, 'Help')}</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToVersionInfoPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/version-info-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.versionInfo', {}, 'Version Info')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button
                        className="settings-line-button"
                        onClick={goToDeveloperInfoPage}
                      >
                        <div className="settings-line-button-content">
                          <img
                            src="/images/svg/developer-info-icon.svg"
                            alt=""
                            className="settings-line-icon"
                          />
                          <span>{t('SettingsModal.developerInfo', {}, 'Developer Info')}</span>
                        </div>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`settings-page ${currentPage === 'devices' ? 'active' : ''}`}>
                <DevicesPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'auto-logout' ? 'active' : ''}`}>
                <AutoLogoutPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'chat-expiry-reminder' ? 'active' : ''}`}>
                <ChatExpiryReminderPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'version-info' ? 'active' : ''}`}>
                <VersionInfoPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'developer-info' ? 'active' : ''}`}>
                <DeveloperInfoPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'first-contact-permissions' ? 'active' : ''}`}>
                <FirstContactPermissionsPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'ignored-users' ? 'active' : ''}`}>
                <IgnoredUsersPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'notifications' ? 'active' : ''}`}>
                <NotificationsSettingsPage onBack={goBackToSettings} />
              </div>

              {currentPage === 'fhir-account' && (
                <div className="settings-page active">
                  <FHIRAccountPage onBack={goBackToSettings} />
                </div>
              )}

              {currentPage === 'chat-backup' && (
                <div className="settings-page active">
                  <ChatBackupPage
                    onBack={goBackToSettings}
                    onShowRecoveryKeyModal={() => {
                      // NED-4003: unverified-with-backup device → reuse the EXISTING recovery-key
                      // overlay (same as skip-at-login). Return to the Settings list underneath so
                      // closing/skipping the modal never lands on the chat-backup State-A surface.
                      setShowRecoveryKeyModal(true);
                      setCurrentPage('settings');
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {showRecoveryKeyModal && (
      <RecoveryKeyModal
        validator={recoveryValidator}
        onSuccess={handleRecoverySuccess}
        onSkip={handleRecoverySkip}
        onReset={handleRecoveryReset}
        allowSkip={true}
        allowReset={true}
      />
    )}
    </>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialPage: PropTypes.string,
};

export default SettingsModal;